import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, Signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { User } from '@shared/graphql/generated';
import { CallbackModalStoreService } from '@shared/modals/callback-modal/shared/callback-modal-store.service';
import { CallbackType } from '@shared/modals/callback-modal/shared/callback-type';
import { NavAction } from '@shared/models/nav-action';
import { ActionLink, NavLink, NavLinkWithIcon } from '@shared/models/nav-link';
import { AuthModalService } from '@shared/services/auth-modal.service';
import { UserStoreService } from '@shared/stores/user-store.service';

@Component({
  selector: 'evland-footer',
  templateUrl: './footer.component.html',
  styleUrls: [ './footer.component.scss' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    NgOptimizedImage,
    MatIcon,
  ]
})
export class FooterComponent {
  private authModal = inject(AuthModalService);
  private callbackModal = inject(CallbackModalStoreService);
  private userStore = inject(UserStoreService);

  contentLinkGroups: Signal<{ label: string, links: (ActionLink | NavLink)[] }[]>;
  currentUser: Signal<User>;

  importantNavLinks: NavLink[] = [
    { href: '/terms-and-conditions', label: $localize`Правила користування сайтом` },
    { href: '/terms-and-conditions', fragment: 'privacyPolicy', label: $localize`Політика конфіденційності` },
    { href: '/public-offer', label: $localize`Публічна оферта` },
  ];
  socialNavLinks: NavLinkWithIcon[] = [
    {
      href: 'https://www.facebook.com/charge.spy/',
      label: $localize`Ми у Facebook`,
      icon: 'social:facebook'
    },
    { href: 'https://www.instagram.com/chargespy', label: $localize`Ми у Instagram`, icon: 'social:instagram' },
    { href: 'https://t.me/chademoforchina', label: $localize`Наш Telegram-чат`, icon: 'social:telegram' },
    { href: 'https://www.youtube.com/@chargespy', label: $localize`Ми на YouTube`, icon: 'social:youtube' },
  ];
  year: number = (new Date()).getFullYear();

  constructor() {
    this.initSignals();
  }

  onActionClick(action: NavAction): void {
    switch (action) {
      case NavAction.SignIn:
      case NavAction.SignUp:
        this.authModal.show(action);
        break;
    }
  }

  openCallbackModal(): void {
    this.callbackModal.show({
      type: CallbackType.Consultation,
      title: $localize`Замовити консультацію`,
      action_label: $localize`Замовити`,
      subject: $localize`Консультація`,
    });
  }

  private initSignals(): void {
    this.currentUser = this.userStore.currentS;

    this.contentLinkGroups = computed(() => {
      const currentUser = this.currentUser();

      return [
        {
          label: $localize`Навігація`,
          links: [
            { href: '/catalog', label: $localize`Каталог` },
            { href: '/warehouse', label: $localize`В наявності` },
            { href: '/offers', label: $localize`З пробігом` },
            { href: '/reviews', label: $localize`Відгуки`, badge: 'NEW' },
            { href: '/financing', label: $localize`Фінансування` },
            { href: '/euro-charge', label: $localize`EuroCharge™` },
            { href: '/zeekr-check', label: $localize`Перевірка ZEEKR` },
            { href: '/about-us', label: $localize`Про нас` },
            { href: '/contact-us', label: $localize`Контакти` },
          ],
        },
        {
          label: $localize`Користувач`,
          links: [
            ...(!!currentUser ? [
              { href: '/account/personal', label: $localize`Особиста інформація` },
              { href: '/account/favorite', label: $localize`Вибране` },
              { href: '/account/accounts', label: $localize`Облікові записи та зв'язки` },
            ] : [
              { action: NavAction.SignUp, label: $localize`Реєстрація` },
              { action: NavAction.SignIn, label: $localize`Вхід` },
            ]),
            { href: '/comparison', label: $localize`Порівняння` },
          ],
        },
      ];
    });
  }
}
